import { Fragment } from "react";
import { monthlySalary, WEEKS_PER_MONTH, type PayrollCycle } from "@/utils/payroll";
import { formatMoney, plural } from "@/utils/format";
import { currencyLabel } from "@/utils/currency";

interface SchoolMeta {
  name: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  schoolYear: string;
}

interface Props {
  cycles: PayrollCycle[];
  scopeLabel: string;
  school: SchoolMeta;
}

export default function PayrollSheet({ cycles, scopeLabel, school }: Props) {
  const grandPayroll = cycles.reduce((sum, c) => sum + c.payroll, 0);
  const grandHours = cycles.reduce((sum, c) => sum + c.hours, 0);
  const grandCount = cycles.reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="print-sheet w-full overflow-hidden rounded-lg border border-background-200 bg-white text-foreground-900">
      <div className="bg-primary-600 px-4 py-3 text-center">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-background-50 md:text-base">
          {school.name}
        </h2>
        <p className="mt-0.5 text-[11px] text-primary-100">
          {school.address} · {school.city} · {school.phone}
        </p>
      </div>

      <div className="bg-primary-50 px-4 py-2.5 text-center">
        <p className="font-heading text-sm font-semibold uppercase tracking-widest text-primary-900">
          Fiche de la masse salariale par cycle
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-x border-background-200 bg-background-100/70 px-4 py-2 text-xs">
        <span className="font-medium text-foreground-700">
          Session : <span className="text-foreground-950">{school.schoolYear}</span>
        </span>
        <span className="text-foreground-500">{scopeLabel}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] border-collapse text-xs md:text-[13px]">
          <thead>
            <tr className="bg-background-200/80 text-left text-[11px] uppercase tracking-wide text-foreground-700">
              <th className="w-10 border border-background-300 px-2 py-2 text-center font-semibold">N°</th>
              <th className="border border-background-300 px-3 py-2 font-semibold">Enseignant</th>
              <th className="w-24 border border-background-300 px-3 py-2 text-right font-semibold">
                Heures / sem.
              </th>
              <th className="w-32 border border-background-300 px-3 py-2 text-right font-semibold">
                Tarif horaire
              </th>
              <th className="w-36 border border-background-300 px-3 py-2 text-right font-semibold">
                Salaire mensuel
              </th>
            </tr>
          </thead>
          <tbody>
            {cycles.map((cycle, cycleIndex) => (
              <Fragment key={cycle.id}>
                <tr className="bg-secondary-100/70">
                  <td className="border border-background-300 px-2 py-2 text-center font-bold text-foreground-900">
                    {["I", "II", "III", "IV", "V"][cycleIndex] ?? cycleIndex + 1}
                  </td>
                  <td
                    className="border border-background-300 px-3 py-2 font-bold uppercase tracking-wide text-foreground-900"
                    colSpan={4}
                  >
                    Cycle {cycle.label} — {plural(cycle.count, "enseignant")} · {cycle.hours} h / semaine
                  </td>
                </tr>
                {cycle.teachers.map((teacher, index) => (
                  <tr key={teacher.id}>
                    <td className="border border-background-300 px-2 py-1.5 text-center text-foreground-500">
                      {index + 1}
                    </td>
                    <td className="border border-background-300 px-3 py-1.5 text-foreground-800">
                      {teacher.lastName} {teacher.firstName}
                    </td>
                    <td className="border border-background-300 px-3 py-1.5 text-right tabular-nums text-foreground-700">
                      {teacher.weeklyHours ?? 0}
                    </td>
                    <td className="border border-background-300 px-3 py-1.5 text-right tabular-nums text-foreground-700">
                      {teacher.fixedMonthlySalary ? "Fixe" : formatMoney(teacher.hourlyRate ?? 0)}
                    </td>
                    <td className="border border-background-300 px-3 py-1.5 text-right tabular-nums text-foreground-800">
                      {formatMoney(monthlySalary(teacher))}
                    </td>
                  </tr>
                ))}
                <tr className="bg-background-100">
                  <td className="border border-background-300 px-2 py-1.5"></td>
                  <td className="border border-background-300 px-3 py-1.5 font-semibold text-foreground-900">
                    Sous-total Cycle {cycle.label}
                  </td>
                  <td className="border border-background-300 px-3 py-1.5 text-right font-semibold tabular-nums text-foreground-900">
                    {cycle.hours}
                  </td>
                  <td className="border border-background-300 px-3 py-1.5"></td>
                  <td className="border border-background-300 px-3 py-1.5 text-right font-semibold tabular-nums text-foreground-900">
                    {formatMoney(cycle.payroll)}
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-primary-100">
              <td className="border border-primary-300 px-2 py-2.5"></td>
              <td className="border border-primary-300 px-3 py-2.5 font-bold uppercase tracking-wide text-primary-900">
                Masse salariale totale ({grandCount} enseignants)
              </td>
              <td className="border border-primary-300 px-3 py-2.5 text-right font-bold tabular-nums text-primary-900">
                {grandHours} h
              </td>
              <td className="border border-primary-300 px-3 py-2.5"></td>
              <td className="border border-primary-300 px-3 py-2.5 text-right font-bold tabular-nums text-primary-900">
                {formatMoney(grandPayroll)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-x border-background-200 px-4 py-2.5 text-[11px] text-foreground-500">
        <span>Salaire mensuel = heures / semaine × tarif horaire × {WEEKS_PER_MONTH} semaines.</span>
        <span>Devise : {currencyLabel()}</span>
      </div>

      <div className="grid grid-cols-2 gap-8 border border-t-0 border-background-200 px-8 py-7 text-[11px] text-foreground-600">
        <div>
          <p className="mb-7 font-medium text-foreground-700">Le Directeur des études</p>
          <p className="border-t border-dashed border-background-400 pt-1"> </p>
        </div>
        <div>
          <p className="mb-7 font-medium text-foreground-700">La Comptable</p>
          <p className="border-t border-dashed border-background-400 pt-1"> </p>
        </div>
      </div>
    </div>
  );
}